import type { FieldInfoExample, FieldInfoPanel } from '../types/fieldInfoPanel';

/**
 * Opportunity → Project description prompt + insertable worked examples.
 * Must contain no angle brackets — Zod rejects `/<[^>]*>/` on this field.
 * Bullet lines use a leading `• ` so the overlay and rich-HTML insert agree.
 */
export const OPPORTUNITY_PROJECT_DESCRIPTION_PROMPT =
  "Please describe your project needs. Include equipment types and quantities required, cooling and power requirements. If it's an AI project, please include the specific GPU architectures being deployed.";

export const OPPORTUNITY_PROJECT_DESCRIPTION_EXAMPLES_INTRO =
  'Here are some examples to get you started:';

export const OPPORTUNITY_PROJECT_DESCRIPTION_EXAMPLES: readonly FieldInfoExample[] =
  [
    {
      id: 'traditional-compute',
      label: 'Example 1: Traditional Compute',
      text: [
        'Customer has a large computer room that needs cold aisle containment.',
        '',
        '• 24 racks (average 8kW a rack)',
        "• 2 UPS's for redundancy",
        '• Fire suppression needed (not provided by Vertiv)',
        '• Hot Aisle Containment',
        '• 120 mins of runtime',
        '• Dual Busway with a tap box for each per bus bar.',
      ].join('\n'),
    },
    {
      id: 'ai-hgx-b300',
      label: 'Example 2: AI Deployment',
      text: [
        '• Platform: NVIDIA HGX B300, air cooled, 8 GPU servers.',
        '• 8 compute racks with 4 HGX B300 servers per compute rack.',
        '• 1 network rack and 1 storage rack.',
        '• We want this to be 1 row of 10 racks, not a pod layout.',
      ].join('\n'),
    },
    {
      id: 'ai-nvl72-vera-rubin',
      label: 'Example 3: AI Deployment',
      text: [
        'This customer wants to deploy two NVL72 Vera Rubin (224kw total heat load each). We need:',
        '',
        "• 1x MGX 1.1 Rack with CDU150s' in a team work mode similar to the NPS design.",
        "• 4x CDU150's to have 3 for capacity +1 redundancy.",
        '• CDUs should be AC powered.',
        '• Power: 2x 60a 415/240v vertical pdus in the rack with full management.',
      ].join('\n'),
    },
    {
      id: 'ai-mgx-liquid',
      label: 'Example 4: AI Deployment',
      text: [
        '• One MGX compute rack, DC busbar, liquid cooled, approx. 80 kW heat load',
        '• Power shelves per the NVIDIA spec (110 kW class, N+N)',
        '• CDU sized for the above, both a facility water and a liquid to air option would be helpful',
        '• One support rack for network and control plane, incl. 33 kW shelf or AC PDUs',
      ].join('\n'),
    },
    {
      id: 'ai-xe9680',
      label: 'Example 5: AI Deployment',
      text: [
        'This customer is building out 2 AI rows containing XE9680 Dell Servers with 277V power supplies.',
        'They also need 208/120V to the racks for switches and other gear that they have.',
        '',
        '• Each rack has (4) XE9680 with each having (6) input plugs.',
      ].join('\n'),
    },
  ];

export const OPPORTUNITY_PROJECT_DESCRIPTION_EXAMPLE =
  OPPORTUNITY_PROJECT_DESCRIPTION_EXAMPLES[0]?.text ?? '';

export const OPPORTUNITY_PROJECT_DESCRIPTION_INFO: FieldInfoPanel = {
  title: 'Project Description',
  bullets: [OPPORTUNITY_PROJECT_DESCRIPTION_PROMPT],
  examplesIntro: OPPORTUNITY_PROJECT_DESCRIPTION_EXAMPLES_INTRO,
  examples: OPPORTUNITY_PROJECT_DESCRIPTION_EXAMPLES,
};
